// cSpell: ignore IRGBA
import { NgStyle } from "@angular/common";
import { Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { StyleDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { IColorPickerChangeEventData, IRGBA } from "@tylertech/forge";
import { ForgeButtonModule, ForgeColorPickerModule, ForgeLabelValueModule, ForgePopoverModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { EMPTY_GUID, GuidHelper, Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { FocusOnLoadDirective } from "../../../../directives/focusOnLoad.directive";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataPropertyService } from "../../../../services";


@Component({
    selector: "metadata-input-color",
    templateUrl: "./metadata-input-color.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        LayoutAlignDirective,
        LayoutDirective,
        StyleDirective,
        FocusOnLoadDirective,
        ForgeButtonModule,
        ForgeColorPickerModule,
        ForgeLabelValueModule,
        ForgePopoverModule,
        ForgeTooltipModule,
        NgStyle,
    ]
})
export class MetadataInputColorComponent implements OnInit, OnChanges, DoCheck
{
    private readonly $metadataPropertyService = inject(MetadataPropertyService);

    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    compact: boolean;

    @Input()
    label: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    error: string;

    @ViewChild("popup") popup: any;

    @ViewChild("popupContent") popupContent: any;

    /** Accessible in the template */ isViewOnly: boolean;

    opening: boolean = false;

    holdFocus: boolean = false;

    private isInitialized: boolean;
    private _currentValue: IColorPickerChangeEventData;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: string;

    private _valueModel: string;

    private _popupOpen: boolean = false;

    ngOnInit(): void
    {
        this.isInitialized = true;
        this.onModelChanged(this.model);
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        //const model = isNaN(this.model) ? 0 : this.model;
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model": {
                    const modelChange = _changes[key];
                    this.onModelChanged(modelChange.currentValue);
                    break;
                }
            }
        }
    }

    private onModelChanged(value: string): void
    {
        this.unboundModel = value;
        // if model changed, check if we need to update valueModel or selectedItem.
        if (value !== "#ffffff" && !value)
        {
            this.valueModel = null;
            return;
        }
        this.valueModel = value;
    }

    get popupOpen(): boolean
    {
        return this._popupOpen;
    }

    set popupOpen(value: boolean)
    {
        this._popupOpen = value;
    }

    get popupElementId(): string
    {
        return `colorpicker-popup-${this.name}`;
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        this._valueModel = value;
    }

    rgbaToNumber(rgba: IRGBA): number
    {
        return (rgba.r << 16) + (rgba.g << 8) + (rgba.b);
    }

    get name(): string
    {
        return this.propertyMetadata.Name;
    }

    get controlId(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadataModel)) return `color-picker_${this.name}`;
        let id = this.metadataModel.$controlId;
        if (!Utilities.IsNullOrUndefined(id)) return `color-picker_${this.name}_${id}`;
        id = this.metadataModel.$controlId = this.metadataModel.Id;
        if (Utilities.IsNullOrUndefinedOrEmpty(id) || id === EMPTY_GUID)
        {
            id = this.metadataModel.$controlId = GuidHelper.newGuid();
        }
        return `color-picker_${this.name}_${id}`;
    }

    fireValueChanged(): void
    {
        let newValue: string;
        if (!this._currentValue) return;

        if (`#${this._currentValue.hex}` === this.model)
        {
            return;
        }
        else
        {
            newValue = `#${this._currentValue.hex}`;
        }

        if (this.model === newValue || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: newValue });
    }

    valueChanged(color: IColorPickerChangeEventData): void
    {
        this._currentValue = color;
        this.valueModel = `#${color.hex}`;
    }

    focusOut($event: CustomEvent): void
    {
        if ($event.detail.newState === "closed")
        {
            this.fireValueChanged();
        }
    }
}
